import { useSession } from '@airship-platform/airship-auth';
import {
  canEditPrice,
  PriceSection,
  ProductPriceFormValues,
} from '@airship-platform/airship-proposal';
import { FlexBox } from '@airship-platform/jam-web/components';
import { PencilIcon } from '@airship-platform/jam-web/icons';
import IconButton from '@mui/material/IconButton';
import { useState } from 'react';
import { AppointmentProposalOptionProduct } from '../../../data-access/appointmentProposalOptionProducts';
import { useAppointments } from '../../../data-access/appointments';
import { EditProductPriceModal } from '../../proposal-option-items';

type ProductPrice = Pick<
  AppointmentProposalOptionProduct,
  'price' | 'defaultPriceType'
>;

interface SubItemRowProps {
  product: AppointmentProposalOptionProduct;
  isReadOnly: boolean;
  onPriceChange: (price: ProductPrice) => void;
}

const SubItemRowPrice: React.FC<SubItemRowProps> = ({
  product,
  isReadOnly,
  onPriceChange,
}) => {
  const { claims } = useSession();
  const { currentAppointment } = useAppointments();
  const [isPriceModalOpen, setIsPriceModalOpen] = useState(false);

  const onSubmitPrice = (price: ProductPriceFormValues) => {
    onPriceChange({
      price: price.value,
      defaultPriceType: price.priceType,
    });
    setIsPriceModalOpen(false);
  };

  return (
    <FlexBox
      gap={[0, 0, 2]}
      flex={1}
      flexDirection={['column', 'column', 'row']}
    >
      <PriceSection
        value={product.price}
        currency={product.currency}
        quantity={product.quantity}
        hasDiscount={!!product.hasMembership}
        valueWithDiscount={product.membershipPrice}
      />
      {canEditPrice(claims, product) && (
        <IconButton
          disabled={isReadOnly}
          size={'small'}
          sx={{ height: '2rem', width: '2rem' }}
          onClick={() => setIsPriceModalOpen(true)}
        >
          <PencilIcon />
        </IconButton>
      )}

      {isPriceModalOpen && (
        <EditProductPriceModal
          isModalOpen={isPriceModalOpen}
          isLoading={false}
          title={product.name}
          hasMembership={!!currentAppointment?.memberships?.length}
          prices={product.pricebookPrices}
          defaultValues={{
            value: product.price,
            currency: product.currency,
            priceType: product.defaultPriceType,
            taxable: product.taxable,
          }}
          onModalClose={() => setIsPriceModalOpen(false)}
          onSubmitPrice={onSubmitPrice}
        />
      )}
    </FlexBox>
  );
};

export default SubItemRowPrice;
